import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { RadixSelect } from "../ui/select";
import { CUSTOM_SCROLLBAR } from "@/lib/constants";
import GraphDbPropertiesEditor from "./graph-db-properties-editor";
import {
  EMPTY_GRAPH_DB_FORM,
  GRAPH_INDEX_KINDS,
  formPayload,
  formProblem,
  propertiesProblem,
  toProperties,
  type GraphDbForm,
  type GraphDbKind,
} from "@/lib/graph-db-schema";
import {
  useCreateGraphDbItem,
  useUpdateGraphDbItem,
  type GraphDbItem,
} from "@/service-hooks/use-get-graph-db-ontology-service";

interface GraphDbDialogProps {
  isOpen: boolean;
  onClose: () => void;
  kind: GraphDbKind;
  projectUuid: string;
  dataLakeId: string;
  /** Names of the node types already recorded, offered when choosing an edge type's two ends. */
  nodeTypeNames: string[];
  /** When supplied the dialog edits this item. */
  existing?: GraphDbItem;
}

const TITLE: Record<GraphDbKind, string> = {
  "node-type": "node type",
  "edge-type": "edge type",
  index: "index",
};

const fromItem = (item?: GraphDbItem): GraphDbForm => ({
  ...EMPTY_GRAPH_DB_FORM,
  name: item?.name ?? "",
  description: item?.description ?? "",
  properties: toProperties(item?.properties),
  from: item?.from ?? "",
  to: item?.to ?? "",
  indexKind: item?.kind ?? "",
  definition: item?.definition ?? "",
});

export default function GraphDbDialog({
  isOpen,
  onClose,
  kind,
  projectUuid,
  dataLakeId,
  nodeTypeNames,
  existing,
}: GraphDbDialogProps) {
  const [form, setForm] = useState<GraphDbForm>(EMPTY_GRAPH_DB_FORM);
  const create = useCreateGraphDbItem();
  const update = useUpdateGraphDbItem();
  const isEdit = !!existing?.id;
  const isPending = create.isPending || update.isPending;

  useEffect(() => {
    if (isOpen) setForm(fromItem(existing));
  }, [isOpen, existing]);

  const set = <K extends keyof GraphDbForm>(key: K, value: GraphDbForm[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const problem = formProblem(kind, form);
  const propertyError = kind === "index" ? null : propertiesProblem(form.properties);

  // An edge type's ends are plain text, so a name not in the list is allowed (kept as it is, shown as an option).
  const endOptions = useMemo(() => {
    const names = new Set(nodeTypeNames);
    for (const n of [form.from, form.to]) if (n.trim()) names.add(n);
    return [...names].sort().map((n) => ({ value: n, label: n }));
  }, [nodeTypeNames, form.from, form.to]);

  const handleSubmit = async () => {
    if (problem) return;
    const data = formPayload(kind, form);
    try {
      if (isEdit && existing?.id) {
        await update.mutateAsync({ kind, id: existing.id, projectUuid, dataLakeId, data });
      } else {
        await create.mutateAsync({ kind, projectUuid, dataLakeId, data });
      }
      onClose();
    } catch {
      // toast handled in mutation
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Edit ${TITLE[kind]}` : `Add ${TITLE[kind]}`}</DialogTitle>
          <DialogDescription className="sr-only">Record a {TITLE[kind]} of the graph database.</DialogDescription>
        </DialogHeader>

        <div className={`space-y-4 overflow-y-auto flex-1 pr-1 ${CUSTOM_SCROLLBAR}`}>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-white">
              Name <span className="text-error">*</span>
            </label>
            <Input
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              placeholder={kind === "edge-type" ? "e.g. relationship type name" : kind === "index" ? "index name" : "e.g. label name"}
              className="bg-surface text-white"
            />
          </div>

          {kind === "edge-type" && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-white">
                  From node type <span className="text-error">*</span>
                </label>
                <RadixSelect
                  options={endOptions}
                  value={form.from}
                  onChange={(v) => set("from", v)}
                  placeholder="Choose…"
                  ariaLabel="From node type"
                  disabled={isPending}
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-white">
                  To node type <span className="text-error">*</span>
                </label>
                <RadixSelect
                  options={endOptions}
                  value={form.to}
                  onChange={(v) => set("to", v)}
                  placeholder="Choose…"
                  ariaLabel="To node type"
                  disabled={isPending}
                />
              </div>
              {endOptions.length === 0 && (
                <p className="col-span-2 text-xs text-muted">Add a node type first, then pick its two ends here.</p>
              )}
            </div>
          )}

          {kind === "index" ? (
            <>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-white">
                  Kind <span className="text-error">*</span>
                </label>
                <RadixSelect
                  options={GRAPH_INDEX_KINDS.map((k) => ({ value: k, label: k }))}
                  value={form.indexKind}
                  onChange={(v) => set("indexKind", v)}
                  placeholder="Choose…"
                  ariaLabel="Index kind"
                  disabled={isPending}
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-white">
                  Definition <span className="text-error">*</span>
                </label>
                <Textarea
                  value={form.definition}
                  onChange={(e) => set("definition", e.target.value)}
                  placeholder="The database's own definition, pasted as it is"
                  className="bg-surface text-white min-h-[96px] font-mono text-xs"
                />
              </div>
            </>
          ) : (
            <>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-white">Description</label>
                <Textarea
                  value={form.description}
                  onChange={(e) => set("description", e.target.value)}
                  placeholder="What this holds (optional)"
                  className="bg-surface text-white min-h-[72px]"
                />
              </div>
              <GraphDbPropertiesEditor
                value={form.properties}
                onChange={(rows) => set("properties", rows)}
                error={propertyError}
                disabled={isPending}
              />
            </>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t border-surface-border">
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!!problem || isPending}
            title={problem ?? undefined}
            className="bg-primary hover:bg-primary-dark text-white"
          >
            {isPending ? (isEdit ? "Saving..." : "Adding...") : isEdit ? "Save" : "Add"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
